import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import {
  appendPage,
  hasMorePages,
  nextPageNumber,
  type Page,
  type PagedListData,
} from '../ts-utils/pagedList'

export interface PagedList<T> {
  /** Every loaded page, flattened, in the server's order. */
  items: T[]
  /** The rows of the most recently loaded page. */
  lastPageItems: T[]
  /** Cold load (or still waiting to be enabled) — nothing to show yet. */
  isLoading: boolean
  isError: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  /**
   * The last next-page request failed. Automatic loading should stop until the
   * user asks again (scroll or refresh), or a failing server is retried in a
   * loop.
   */
  nextPageFailed: boolean
  /** No-op while a page is in flight or the list is exhausted. */
  fetchNextPage: () => void
  /** Refetch page one, discarding appended pages (pull-to-refresh). */
  refetch: () => Promise<unknown>
}

export interface UsePagedListArgs<T> {
  queryKey: readonly unknown[]
  fetchPage: (page: number) => Promise<Page<T>>
  enabled: boolean
  staleTime?: number
  /**
   * Overrides the app-wide `refetchOnWindowFocus: false` for this list. A
   * focus refetch is a refetch like any other: back to a fresh page one.
   */
  refetchOnWindowFocus?: boolean | 'always'
}

/**
 * A list that pages as the user scrolls — the pattern `useEventStream` uses for
 * the schedule lists, generic over the row type.
 *
 * Not `useInfiniteQuery`: under Metro, React Native bundles query-core from
 * source and its import cycle leaves `InfiniteQueryObserver` without
 * `getCurrentResult`, so every screen using it throws on mount (see
 * `useEventStream`).
 *
 * Every loaded page lives in ONE cache entry, and the query function fetches
 * page one only. So a refetch, or an invalidation of the key's prefix, resets
 * the list to a fresh page one — one request, and no stale deeper pages kept
 * beside it. Later pages are appended by `fetchNextPage`.
 */
export const usePagedList = <T>({
  queryKey,
  fetchPage,
  enabled,
  staleTime,
  refetchOnWindowFocus,
}: UsePagedListArgs<T>): PagedList<T> => {
  'use memo'

  const queryClient = useQueryClient()
  const [isFetchingNext, setIsFetchingNext] = useState(false)
  const [nextPageFailed, setNextPageFailed] = useState(false)

  const query = useQuery<PagedListData<T>>({
    queryKey,
    // Page ONE only — a refetch collapses the list back to its first page.
    queryFn: async () => {
      const first = await fetchPage(1)
      setNextPageFailed(false)
      return { pages: [first] }
    },
    enabled,
    staleTime,
    refetchOnWindowFocus,
  })

  const pages = query.data?.pages ?? []
  const items = pages.flatMap((page) => page?.data ?? [])
  const lastPageItems = pages[pages.length - 1]?.data ?? []

  // Read inside the handler, not closed over, so a `fetchNextPage` captured by a
  // scroll listener several renders ago still appends to the CURRENT pages.
  const isAppendingRef = useRef(false)

  const fetchNextPage = () => {
    if (isAppendingRef.current || query.isFetching) {
      return
    }
    const next = nextPageNumber(
      queryClient.getQueryData<PagedListData<T>>(queryKey)
    )
    if (next === null) {
      return
    }

    isAppendingRef.current = true
    setIsFetchingNext(true)
    setNextPageFailed(false)
    fetchPage(next)
      .then((page) => {
        // Re-read: a refetch or invalidation may have replaced the entry while
        // this was in flight, and appending to it would resurrect dropped pages.
        const latest = queryClient.getQueryData<PagedListData<T>>(queryKey)
        if (nextPageNumber(latest) !== next) {
          return
        }
        queryClient.setQueryData<PagedListData<T>>(queryKey, (old) =>
          appendPage(old, page)
        )
      })
      // The page just doesn't append; the list the user is reading stays.
      .catch(() => setNextPageFailed(true))
      .finally(() => {
        isAppendingRef.current = false
        setIsFetchingNext(false)
      })
  }

  return {
    items,
    lastPageItems,
    // A DISABLED query reports `isLoading: false`; the caller is still waiting
    // on whatever gates it, so report loading rather than flash the empty state.
    isLoading: enabled ? query.isLoading : true,
    isError: query.isError,
    hasNextPage: hasMorePages(pages),
    isFetchingNextPage: isFetchingNext,
    nextPageFailed,
    fetchNextPage,
    refetch: query.refetch,
  }
}

export default usePagedList
